import React, { useState, useCallback, useEffect, useRef } from 'react';
import './Build.css';
import Cropper from 'react-easy-crop';
import { getCroppedImg } from '../utils/cropImage';

const months = [
  '1月', '2月', '3月', '4月',
  '5月', '6月', '7月', '8月',
  '9月', '10月', '11月', '12月',
];

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const shuffleImages = (images) => {
  const copiedImages = [...images];

  for (let index = copiedImages.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [copiedImages[index], copiedImages[randomIndex]] = [copiedImages[randomIndex], copiedImages[index]];
  }

  return copiedImages;
};

function MonthGrid({ monthImages, setMonthImages }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentMonthNumber, setCurrentMonthNumber] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [hasUserAdjustedCrop, setHasUserAdjustedCrop] = useState(false);
  const [isMonthEditorVisible, setIsMonthEditorVisible] = useState(false);
  const batchInputRef = useRef(null);

  const selectedMonthCount = months.filter((_, index) => Boolean(monthImages[index + 1])).length;
  const hasSelectedBatch = selectedMonthCount > 0;

  const handleMonthImageUpload = useCallback((monthNumber, imageSrc) => {
    setMonthImages((prev) => ({ ...prev, [monthNumber]: imageSrc }));
  }, [setMonthImages]);

  const handleBatchUpload = useCallback(async (e) => {
    const files = Array.from(e.target.files || []);

    if (!files.length) {
      return;
    }

    const limitedFiles = files.slice(0, 12);

    try {
      const loadedImages = await Promise.all(limitedFiles.map(readFileAsDataUrl));
      const shuffledImages = shuffleImages(loadedImages);

      setMonthImages((prev) => {
        const nextImages = { ...prev };

        shuffledImages.forEach((imageSrc, index) => {
          nextImages[index + 1] = imageSrc;
        });

        return nextImages;
      });

      if (files.length > 12) {
        window.alert('13枚以上選択されたため、先頭12枚のみを使いました。');
      }
    } catch (error) {
      console.error(error);
      window.alert('写真の読み込みに失敗しました。もう一度お試しください。');
    } finally {
      e.target.value = '';
    }
  }, [setMonthImages]);

  const onCropComplete = useCallback((croppedArea, nextCroppedAreaPixels) => {
    void croppedArea;
    setCroppedAreaPixels(nextCroppedAreaPixels);
    setHasUserAdjustedCrop(true);
  }, []);

  useEffect(() => {
    if (selectedImage && croppedAreaPixels && hasUserAdjustedCrop) {
      const saveCroppedImage = async () => {
        try {
          const croppedImage = await getCroppedImg(
            selectedImage,
            croppedAreaPixels,
            zoom
          );
          handleMonthImageUpload(currentMonthNumber, croppedImage);
        } catch (error) {
          console.error(error);
        }
      };

      saveCroppedImage();
    }
  }, [croppedAreaPixels, zoom, hasUserAdjustedCrop, currentMonthNumber, handleMonthImageUpload, selectedImage]);

  const handleUpload = (e, monthNumber) => {
    const file = e.target.files[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result;
      img.onload = () => {
        setCroppedAreaPixels({
          x: 0,
          y: 0,
          width: img.width,
          height: img.height,
        });

        setSelectedImage(reader.result);
        setCurrentMonthNumber(monthNumber);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setHasUserAdjustedCrop(false);
        setIsMonthEditorVisible(true);
      };
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <section id="month-grid" className="month-grid">
      <div className="month-grid-panel">
        <p className="month-grid-step">step01</p>
        <h4 className="month-grid-title">
          12枚のお気に入りの
          <br />
          写真をえらぼう
        </h4>
        <p className="month-grid-description">
          選んだ12枚はランダムで12ヶ月分に振り分けられます。
          <br />
          あとから、月ごとに写真の入れ替えが可能です。
        </p>

        <input
          ref={batchInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleBatchUpload}
          style={{ display: 'none' }}
        />

        <button
          type="button"
          className={`month-grid-upload-button${hasSelectedBatch ? ' is-selected' : ''}`}
          onClick={() => {
            if (hasSelectedBatch) {
              setIsMonthEditorVisible(true);
            }
            if (batchInputRef.current) {
              batchInputRef.current.click();
            }
          }}
        >
          <span className="month-grid-upload-main">
            <span>{hasSelectedBatch ? '選択済み' : '写真を選択'}</span>
            <img
              src={hasSelectedBatch ? '/media/icon-check.svg' : '/media/icon-upload.svg'}
              alt=""
              aria-hidden="true"
              className="month-grid-upload-icon-image"
            />
          </span>
          {hasSelectedBatch && (
            <span className="month-grid-upload-sub">写真を選び直したいときはタップ</span>
          )}
        </button>
      </div>

      {isMonthEditorVisible && selectedImage && (
        <div className="month-grid-editor">
          <p className="month-grid-editor-title">
            {currentMonthNumber}
            月の写真を調整中
          </p>
          <div className="month-grid-cropper">
            <Cropper
              image={selectedImage}
              crop={crop}
              maxZoom={7}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          </div>
          <p className="month-grid-editor-help">写真を動かしたり拡大して、見せたい位置に合わせてください。</p>
        </div>
      )}

      {hasSelectedBatch && isMonthEditorVisible && (
        <div className="month-grid-months">
          <div className="month-grid-months-head">
            <h5 className="month-grid-months-title">月ごとの写真</h5>
            <p className="month-grid-months-caption">タップするとその月の写真を差し替えできます</p>
          </div>

          <div className="month-grid-list">
            {months.map((monthName, index) => {
              const monthNumber = index + 1;
              const imageSrc = monthImages[monthNumber];
              const isActive = currentMonthNumber === monthNumber;

              return (
                <div className="month-grid-item" key={monthNumber}>
                  <button
                    type="button"
                    className={`month-card${imageSrc ? ' is-filled' : ''}${isActive ? ' is-active' : ''}`}
                    style={imageSrc ? { backgroundImage: `url(${imageSrc})` } : {}}
                    onClick={() => document.getElementById(`month-upload-${monthNumber}`).click()}
                  >
                    <span className="month-card-label">{monthName}</span>
                    <span className="month-card-helper">{imageSrc ? '入れ替え' : '追加'}</span>
                  </button>

                  <input
                    type="file"
                    accept="image/*"
                    id={`month-upload-${monthNumber}`}
                    style={{ display: 'none' }}
                    onChange={(e) => handleUpload(e, monthNumber)}
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

export default MonthGrid;
